import { without } from "../fields";
import { type AxialCoord, hexDistance, hexEquals } from "../hex";
import { ownWorkingCity, workingCities } from "../land/cities";
import { canTake } from "../land/territory";
import { nearestTo } from "../pick";
import { findHex, isOwnGround, replaceHex, unitById } from "../run/board";
import { kingOf } from "../run/pools";
import { humanKings, isHeldUnit, isSeatUnit } from "../run/seats";
import { sameOwner, sideOf } from "../run/sides";
import type { FactionId, HexState, RunState, Unit, UnitKind } from "../types/state";
import { isBroken } from "../units/morale";
import { orderUnit, road } from "../units/routes";
import { replaceUnit, updateUnits } from "../units/unit";
import { cityTurn, yardTrains } from "./cityDuty";

/**
 * City Command's verbs (CC1, D-072): every city's one arrow, its yard's kind, hand orders, and the
 * readers of a city's column and land. Which command means which verb is the door's (`VERBS` in
 * ../command.ts, each verb's `city` entry).
 */

/**
 * Whether the kingdom's King commands through his cities (CC1): the block is on, the kingdom has
 * a King, and a human holds it (`scope: "humans"`, the scripts keep the court) or the block binds
 * every kingdom (`scope: "all"`, CC4: the scripts play City Command too).
 */
export function isCityCommand(state: RunState, faction: FactionId): boolean {
  const rules = state.rules.cityCommand;
  return (
    rules !== undefined &&
    state.kings?.[faction] !== undefined &&
    (rules.scope === "all" || humanKings(state).includes(faction))
  );
}

/** The King fled less than `shakenTicks` ago (CC1): he gives no courage and his city no presence. */
export function isShaken(state: RunState, faction: FactionId): boolean {
  const until = kingOf(state, faction)?.shakenUntil;
  return until !== undefined && state.tick < until;
}

/** The unshaken city-command King stands on this city (CC1): it trains in `presencePercent` of the time. */
export function kingPresentIn(state: RunState, faction: FactionId, city: AxialCoord): boolean {
  const king = kingOf(state, faction);
  return (
    king !== undefined &&
    hexEquals(king.coord, city) &&
    isCityCommand(state, faction) &&
    !isShaken(state, faction)
  );
}

/**
 * A city's column (CC1): the living soldiers of its owner whose home it is, in id order. A
 * rival's soldiers homed on it before it fell are not its owner's to count, nor is a soldier
 * seat's unit (K1): it is dealt the city's duty while its script plays it, outside the column.
 */
export function cityColumn(state: RunState, city: AxialCoord): Unit[] {
  const owner = findHex(state, city)?.owner;
  return state.units.filter(
    (unit) => unit.owner === owner && homedAt(unit, city) && !isSeatUnit(state, unit.id),
  );
}

/** The unit marches in the column of the city at `city` (CC1): that city is its home. */
export function homedAt(unit: Unit, city: AxialCoord): boolean {
  return unit.home !== undefined && hexEquals(unit.home, city);
}

/** The city's column holds `unitsPerCity` soldiers of its own: its yard trains no more (CC1). */
export function isColumnFull(state: RunState, city: AxialCoord): boolean {
  return cityColumn(state, city).length >= state.rules.unitsPerCity;
}

/**
 * The land a city works (CC1): its kingdom's unrazed hexes within `cityReach` of it that no other
 * working city of the kingdom is nearer to (ties to map order), its own hex among them. The
 * partition `mayorOf` makes over mayored cities, made over every working city.
 */
export function cityLandOf(state: RunState, faction: FactionId, city: AxialCoord): HexState[] {
  const cities = workingCities(state, faction);
  return state.hexes.filter((hex) => {
    if (!isOwnGround(hex, faction)) {
      return false;
    }
    const reaching = cities.filter(
      (candidate) => hexDistance(candidate.coord, hex.coord) <= state.rules.cityReach,
    );
    const nearest = nearestTo(reaching, hex.coord);
    return nearest !== undefined && hexEquals(nearest.coord, city);
  });
}

/**
 * A soldier the King sent by hand (CC1): it holds where he sent it, out of its city's column,
 * until its city's arrow is set after his order (`pointCity`) or he stops it (`releaseUnit`);
 * both hand it back as the city's. Keyed on who gave the order, not on `orderedTick`, which a
 * break stamps too.
 */
export function isHandHeld(state: RunState, unit: Unit): boolean {
  return unit.orderedBy === "king" && isCityCommand(state, unit.owner);
}

/** Standing still on land it is taking by standing (CC1): the duty leaves it to finish. */
export function isTaking(state: RunState, unit: Unit): boolean {
  if (unit.destination) {
    return false;
  }
  return canTake(state, unit.owner, unit.coord);
}

/** The own working city at `coord` of a city-command kingdom (CC1); undefined otherwise. */
export function commandedCity(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
): HexState | undefined {
  return state.status === "running" && isCityCommand(state, faction)
    ? ownWorkingCity(state, faction, coord)
    : undefined;
}

/** Whether a city's arrow can point at `at` (CC1): the city itself (Home), or land its column has a road to. */
export function arrowReaches(
  state: RunState,
  faction: FactionId,
  city: AxialCoord,
  at: AxialCoord,
): boolean {
  return (
    hexEquals(city, at) || road(state.hexes, sideOf(state.rules, faction), city, at) !== undefined
  );
}

/** The soldiers of a city's column the King holds by hand march with it again (CC1): their orders are the city's. */
function recallHands(state: RunState, faction: FactionId, city: AxialCoord): RunState {
  return updateUnits(state, (unit) =>
    !sameOwner(unit.owner, faction) || unit.orderedBy !== "king" || !homedAt(unit, city)
      ? unit
      : { ...unit, orderedBy: "mayor" },
  );
}

/**
 * A city-command King points one of his working cities (CC1): its arrow goes to `at`, or, `at`
 * the city itself, Home (no arrow); `arrowTick` becomes now, the soldiers he sent by hand come
 * back to the column, and the city's free soldiers take their duty at once. Same object back for
 * a city not his, a target off the map, razed or out of the city's road, and for the same arrow
 * set again in the same tick with nobody of its column held by hand.
 */
export function pointCity(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  at: AxialCoord,
): RunState {
  const city = commandedCity(state, faction, coord);
  const target = findHex(state, at);
  if (!city || !target || target.razed || !arrowReaches(state, faction, coord, at)) {
    return state;
  }
  const home = hexEquals(coord, at);
  const same =
    (home ? city.arrow === undefined : city.arrow !== undefined && hexEquals(city.arrow, at)) &&
    city.arrowTick === state.tick &&
    !cityColumn(state, coord).some((unit) => isHandHeld(state, unit));
  if (same) {
    return state;
  }
  const rest = without(city, "arrow");
  const pointed: HexState = home
    ? { ...rest, arrowTick: state.tick }
    : { ...rest, arrow: at, arrowTick: state.tick };
  return cityTurn(recallHands(replaceHex(state, city, pointed), faction, coord), faction, coord);
}

/** Every working city of a city-command King points at `at` (CC1), one command; the city on `at` goes Home. */
export function pointCities(state: RunState, faction: FactionId, at: AxialCoord): RunState {
  let next = state;
  for (const city of workingCities(state, faction)) {
    next = pointCity(next, faction, city.coord, at);
  }
  return next;
}

/** The kind a city of a city-command King trains (CC1): a kind, "pause", or Auto (absent). No training here. */
export function setCityMakes(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  makes: UnitKind | "pause" | undefined,
): RunState {
  const city = commandedCity(state, faction, coord);
  if (!city || city.makes === makes) {
    return state;
  }
  const rest = without(city, "makes");
  return replaceHex(state, city, makes === undefined ? rest : { ...rest, makes });
}

/** The train verb of a city-command King (CC1): the city's kind, and a soldier of it at once when the yard can. */
export function trainCityKind(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  kind: UnitKind,
): RunState {
  const set = setCityMakes(state, faction, coord, kind);
  return commandedCity(set, faction, coord) ? yardTrains(set, faction, coord) : set;
}

/**
 * The stop verb of a city-command King (CC1): the soldier drops its road and, held by his hand,
 * goes back to its city's column (its order is the city's again) for the next duty.
 */
export function releaseUnit(state: RunState, faction: FactionId, unitId: number): RunState {
  const unit = unitById(state, unitId);
  if (
    state.status !== "running" ||
    !unit ||
    !sameOwner(unit.owner, faction) ||
    isHeldUnit(state, unitId) ||
    isBroken(state, unit)
  ) {
    return state;
  }
  const held = isHandHeld(state, unit);
  if (unit.destination === undefined && !held) {
    return state;
  }
  const standing = without(unit, "destination");
  return replaceUnit(state, unit, held ? { ...standing, orderedBy: "mayor" } : standing);
}

/**
 * The order verb of a city-command King (CC1): the soldier walks where he sends it and holds
 * there by his hand. Sent where its duty already walks it, it keeps that road and is his from
 * now on, so he can say "hold there" too. Same object back when the order is refused.
 */
export function handOrder(
  state: RunState,
  faction: FactionId,
  unitId: number,
  to: AxialCoord,
): RunState {
  const ordered = orderUnit(state, faction, unitId, to, "king");
  const unit = unitById(state, unitId);
  if (
    ordered !== state ||
    state.status !== "running" ||
    !unit ||
    !sameOwner(unit.owner, faction) ||
    unit.commander ||
    unit.destination === undefined ||
    !hexEquals(unit.destination, to) ||
    isHeldUnit(state, unitId) ||
    isBroken(state, unit) ||
    isHandHeld(state, unit)
  ) {
    return ordered;
  }
  return replaceUnit(state, unit, { ...unit, orderedTick: state.tick, orderedBy: "king" });
}
